import { strToU8, zipSync } from 'fflate';
import {
  DASHBOARD_ASSETS_PATH,
  contentSecurityPolicy,
  dashboardData,
  dashboardTitle,
  dashboardTitleOf,
  fontFaceRule,
  libraryStyles,
  prependPolicy,
  sanitizeDashboard,
  serializeDashboard,
  type DashboardAsset,
  type DashboardData,
} from './dashboard-document';

type DashboardSource = { name?: string; kind?: string; retrievedAt?: string; ref?: string };

const ASSETS_DIR = 'assets/';
const DATA_SCRIPT = 'data/dashboard-data.js';

const slugify = (value: string) =>
  value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/, '') || 'dashboard';

/** File name without extension: data block title, else `<title>`, else `dashboard`. */
export const dashboardFileName = (source: string) => slugify(dashboardTitle(source));

const fetchFile = async (file: string, integrity?: string) => {
  // Same pinned copies the preview uses; the browser checks the SRI value while fetching.
  const response = await fetch(`${window.location.origin}${DASHBOARD_ASSETS_PATH}${file}`, {
    integrity,
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`Dashboard asset ${file} failed with status ${response.status}`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

/** Each declared asset and its licence files, keyed by their path in the export folder. */
const assetFiles = async (assets: DashboardAsset[]) => {
  const files = assets.flatMap((asset) => [
    { file: asset.file, integrity: asset.integrity },
    ...(asset.licenses ?? []).map((file) => ({ file, integrity: undefined })),
  ]);
  const bytes = await Promise.all(files.map(({ file, integrity }) => fetchFile(file, integrity)));
  return Object.fromEntries(files.map(({ file }, index) => [`${ASSETS_DIR}${file}`, bytes[index]]));
};

/**
 * Recreates the `klax-dashboard-data` block right after its own `<script src>`, synchronously,
 * so dashboard code reads it unchanged. A script file loads from file:// under `'self'`;
 * fetching data.json would not.
 */
export const dashboardDataScript = (json: string) =>
  "(function(){var s=document.createElement('script');s.type='application/json';" +
  `s.id='klax-dashboard-data';s.textContent=${JSON.stringify(json).replace(/</g, '\\u003c')};` +
  'document.currentScript.after(s);})();\n';

/** Same sanitising as the preview; assets and data load from relative files, the network stays denied. */
const exportedDashboard = (source: string) => {
  const { document, assets } = sanitizeDashboard(source);
  const scripts = assets.filter((asset) => asset.type === 'script');
  const fonts = assets.filter((asset) => asset.type === 'font');
  document.head.prepend(
    ...fonts.map((font) => {
      const style = document.createElement('style');
      style.textContent = fontFaceRule(font, `${ASSETS_DIR}${font.file}`);
      return style;
    }),
    ...libraryStyles(document, assets),
    ...scripts.map((asset) => {
      const script = document.createElement('script');
      script.setAttribute('src', `${ASSETS_DIR}${asset.file}`);
      return script;
    })
  );
  const block = document.getElementById('klax-dashboard-data');
  const dataJson = block?.textContent ?? '';
  if (block) {
    const script = document.createElement('script');
    script.setAttribute('src', DATA_SCRIPT);
    block.replaceWith(script);
  }
  prependPolicy(
    document,
    contentSecurityPolicy({ fontSrc: fonts.length ? ["'self'"] : [] }).replace(
      "script-src 'unsafe-inline'",
      "script-src 'self' 'unsafe-inline'"
    )
  );
  return { html: serializeDashboard(document), assets, dataJson: block ? dataJson : null };
};

const readme = (
  title: string,
  data: DashboardData | null,
  hasAssets: boolean,
  exportedAt: Date
) => {
  const lines = [
    `# ${title || 'Dashboard'}`,
    '',
    `Exported from KLAX on ${exportedAt.toISOString()}.`,
  ];
  if (typeof data?.generatedAt === 'string') lines.push(`Data generated at ${data.generatedAt}.`);
  const folders = [hasAssets && 'its libraries from `assets/`', data && 'its data from `data/`']
    .filter(Boolean)
    .join(' and ');
  lines.push(
    '',
    'Open `index.html` in a browser; it works offline.' +
      (folders ? ` Keep the folder structure: \`index.html\` loads ${folders}.` : ''),
    '',
    data
      ? '`data.json` is a copy of the data shown; editing it does not change the dashboard.'
      : 'This dashboard has no data block, so there is no `data.json`.'
  );
  const sources = Array.isArray(data?.sources) ? (data.sources as DashboardSource[]) : [];
  if (sources.length) {
    lines.push('', '## Sources', '');
    sources.forEach((item) => {
      const details = [item.kind, item.retrievedAt && `retrieved ${item.retrievedAt}`, item.ref]
        .filter(Boolean)
        .join(', ');
      lines.push(`- ${item.name ?? 'Unnamed source'}${details ? ` (${details})` : ''}`);
    });
  }
  const guidelines = Array.isArray(data?.guidelines) ? data.guidelines : [];
  if (guidelines.length) {
    lines.push('', '## Design guidelines', '');
    guidelines.forEach((guideline) => lines.push(`- ${String(guideline)}`));
  }
  return `${lines.join('\n')}\n`;
};

/**
 * `<slug>/index.html`, `assets/` (declared assets with their licences), `data/dashboard-data.js`
 * and `data.json` (when the dashboard has a data block) and `README.md`.
 */
export const dashboardZip = async (
  source: string,
  { exportedAt = new Date() } = {}
): Promise<Uint8Array> => {
  const { html, assets, dataJson } = exportedDashboard(source);
  const parsed = new DOMParser().parseFromString(source, 'text/html');
  const data = dashboardData(parsed);
  const files: Record<string, Uint8Array> = {
    'index.html': strToU8(html),
    ...(await assetFiles(assets)),
    'README.md': strToU8(readme(dashboardTitleOf(parsed), data, assets.length > 0, exportedAt)),
  };
  if (dataJson !== null) files[DATA_SCRIPT] = strToU8(dashboardDataScript(dataJson));
  if (data) files['data.json'] = strToU8(`${JSON.stringify(data, null, 2)}\n`);
  const folder = dashboardFileName(source);
  return zipSync(
    Object.fromEntries(Object.entries(files).map(([name, bytes]) => [`${folder}/${name}`, bytes]))
  );
};

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Saves the dashboard as `<slug>.zip`. */
export const downloadDashboard = async (source: string) => {
  // fflate returns a plain ArrayBuffer-backed Uint8Array; its typings allow shared buffers.
  const zip = (await dashboardZip(source)) as Uint8Array<ArrayBuffer>;
  saveBlob(new Blob([zip], { type: 'application/zip' }), `${dashboardFileName(source)}.zip`);
};
